import React from 'react';
import dayjs from 'dayjs';

import { deleteCalendarEvent, fetchCalendarEvent, fetchCalendarEvents } from '../../../../../api/calendarApi';
import {
  buildMiniCalendarBadgeDays,
  filterVisibleEvents,
  getAssignedMemberIds,
  rawToFullCalendarEvents,
} from '../newCalendar.helpers';
import { useOptimisticEventCompletion } from './useOptimisticEventCompletion';

const MEMBER_ASSIGNMENT_FIELDS = ['assigned_hacs', 'assignedHacs', 'hacs', 'tags'];

const hasMemberAssignmentData = (event) =>
  MEMBER_ASSIGNMENT_FIELDS.some((field) => Object.prototype.hasOwnProperty.call(event || {}, field));

function getRequestedRange({ selectedDate, visibleRange }) {
  const monthStart = dayjs(selectedDate).startOf('month');
  const monthEnd = dayjs(selectedDate).endOf('month');
  const visibleStart = dayjs(visibleRange?.start ?? monthStart).startOf('day');
  const visibleEnd = dayjs(visibleRange?.end ?? monthEnd.add(1, 'day')).subtract(1, 'day').endOf('day');

  return {
    requestStart: visibleStart.isBefore(monthStart, 'day') ? visibleStart : monthStart,
    requestEnd: visibleEnd.isAfter(monthEnd, 'day') ? visibleEnd : monthEnd,
  };
}

function extractCalendarRows(response) {
  return Array.isArray(response?.data) ? response.data : response?.data?.events ?? [];
}

export function useCalendarEvents({ token, selectedDate, visibleRange, onlyWithHacs, showSystemTasks, catChecks, advancedFilters }) {
  const [badgeLoading, setBadgeLoading] = React.useState(false);
  const [filterDetailsLoading, setFilterDetailsLoading] = React.useState(false);
  const [sourceEvents, setSourceEvents] = React.useState([]);
  const [eventDetailsById, setEventDetailsById] = React.useState({});
  const [reloadKey, setReloadKey] = React.useState(0);
  const {
    confirmCompletion,
    events: effectiveEvents,
    reconcileConfirmedCompletions,
    resetCompletions,
    rollbackCompletion,
    startCompletion,
  } = useOptimisticEventCompletion(sourceEvents);

  const selectedMemberFilterKey = (advancedFilters?.memberIds || [])
    .map(String)
    .sort()
    .join('|');

  React.useEffect(() => {
    if (!token || !selectedMemberFilterKey) {
      setFilterDetailsLoading(false);
      return undefined;
    }

    const eventsWithoutAssignmentData = effectiveEvents.filter((event) => {
      const eventId = event?.id;
      if (eventId === undefined || eventId === null) return false;
      if (Object.prototype.hasOwnProperty.call(eventDetailsById, String(eventId))) return false;
      return !hasMemberAssignmentData(event) || getAssignedMemberIds(event).length === 0;
    });

    if (eventsWithoutAssignmentData.length === 0) {
      setFilterDetailsLoading(false);
      return undefined;
    }

    let alive = true;
    setFilterDetailsLoading(true);

    Promise.all(eventsWithoutAssignmentData.map(async (event) => {
      const eventId = String(event.id);
      try {
        const response = await fetchCalendarEvent(token, event.id);
        const details = response?.data && typeof response.data === 'object' ? response.data : {};
        return [eventId, details];
      } catch (_error) {
        return [eventId, {}];
      }
    })).then((entries) => {
      if (!alive) return;
      setEventDetailsById((current) => ({ ...current, ...Object.fromEntries(entries) }));
    }).finally(() => {
      if (alive) setFilterDetailsLoading(false);
    });

    return () => {
      alive = false;
    };
  }, [effectiveEvents, eventDetailsById, selectedMemberFilterKey, token]);

  const eventsForFiltering = React.useMemo(
    () => effectiveEvents.map((event) => {
      const details = eventDetailsById[String(event?.id)] || null;
      return details ? { ...event, ...details, id: event.id ?? details.id } : event;
    }),
    [effectiveEvents, eventDetailsById]
  );

  const calendarEvents = React.useMemo(() => {
    const visibleEvents = filterVisibleEvents(eventsForFiltering, {
      onlyWithHacs,
      showSystemTasks,
      catChecks,
      advancedFilters,
    });
    const { requestStart, requestEnd } = getRequestedRange({
      selectedDate,
      visibleRange,
    });

    return visibleEvents.flatMap((event) => rawToFullCalendarEvents(event, {
      start: requestStart,
      end: requestEnd,
    }));
  }, [advancedFilters, catChecks, eventsForFiltering, onlyWithHacs, selectedDate, showSystemTasks, visibleRange]);

  const badgeDays = React.useMemo(
    () => buildMiniCalendarBadgeDays(effectiveEvents, {
      monthStart: dayjs(selectedDate).startOf('month'),
      monthEnd: dayjs(selectedDate).endOf('month'),
    }),
    [effectiveEvents, selectedDate]
  );

  const refreshCalendar = React.useCallback(() => {
    setReloadKey((currentKey) => currentKey + 1);
  }, []);

  const confirmEventCompletion = React.useCallback((eventId) => {
    confirmCompletion(eventId);
    setReloadKey((currentKey) => currentKey + 1);
  }, [confirmCompletion]);

  const executeDeleteTask = React.useCallback(async (tokenArg, _userId, entityId) => {
    if (!tokenArg) throw new Error('Missing token');
    if (!entityId) throw new Error('Missing entityId');

    await deleteCalendarEvent(tokenArg, entityId);
  }, []);

  React.useEffect(() => {
    if (!token) {
      setSourceEvents([]);
      resetCompletions();
      setBadgeLoading(false);
      return;
    }

    const { requestStart, requestEnd } = getRequestedRange({
      selectedDate,
      visibleRange,
    });

    let alive = true;
    setBadgeLoading(true);

    (async () => {
      try {
        const response = await fetchCalendarEvents(
          token,
          requestStart.format('YYYY-MM-DD'),
          requestEnd.format('YYYY-MM-DD')
        );
        if (!alive) return;

        const rows = extractCalendarRows(response);
        setSourceEvents(rows);
        setEventDetailsById({});
        reconcileConfirmedCompletions();
      } catch (_error) {
        // Keep the last known events and optimistic state on transient refresh failures.
      } finally {
        if (alive) setBadgeLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [token, selectedDate, visibleRange, reloadKey, reconcileConfirmedCompletions, resetCompletions]);

  return {
    badgeDays,
    badgeLoading: badgeLoading || filterDetailsLoading,
    calendarEvents,
    confirmEventCompletion,
    executeDeleteTask,
    refreshCalendar,
    rollbackEventCompletion: rollbackCompletion,
    startEventCompletion: startCompletion,
  };
}
